import useGetLoginMessage from '@/hooks/api/useGetLoginMessage';
import useSetLoading from '@/hooks/useSetLoading';
import { LoginMessageState } from '@/states/loginMessageState';
import { LoginMessageResponse } from '@/types/api/mypage/loginMessage';
import { isLoginSuccessful } from '@/utils/cognito';
import url from '@/utils/config';
import { ApiUrl } from '@/utils/constants';
import { AxiosResponse } from 'axios';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useRecoilState } from 'recoil';

/**
 * ログイン後に表示する重要バナーを取得し、閉じたものだけを sessionStorage で非表示に保つ通知領域。
 * 画面遷移後も一度閉じたバナーを再表示しないことで、同一セッション中のノイズを減らしている。
 */
const LoginMessageBanner = () => {
  const router = useRouter();
  const [loginMessage, setLoginMessage] = useRecoilState<LoginMessageResponse>(LoginMessageState);
  const { execute: getLoginMessage } = useGetLoginMessage();
  const { startLoading, finishLoading } = useSetLoading();

  useEffect(() => {
    if (!isLoginSuccessful()) {
      callAPIGetLoginMessage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath]);

  const callAPIGetLoginMessage = async () => {
    // ログインメッセージ取得後、利用者が閉じたバナーだけを除外して表示対象を絞る。
    try {
      startLoading(ApiUrl.Read.LOGIN_MESSAGE);
      const { data }: AxiosResponse<LoginMessageResponse, any> = await getLoginMessage();
      const hiddenBannerIndexes = JSON.parse(sessionStorage.getItem('hiddenBanners') || '[]');
      const visibleBanners = data.banners.filter(
        (banner) => !hiddenBannerIndexes.includes(banner.index)
      );
      setLoginMessage((prevLoginMessage) => ({
        ...prevLoginMessage,
        banners: visibleBanners,
      }));
      return { data, isError: false };
    } catch (error: any) {
      return { isError: true };
    } finally {
      finishLoading(ApiUrl.Read.LOGIN_MESSAGE);
    }
  };

  const handleClose = (id: number) => {
    // 閉じた index を保存しておき、同一セッション内では再表示しない。
    const hiddenBannerIndexes = JSON.parse(sessionStorage.getItem('hiddenBanners') || '[]');
    hiddenBannerIndexes.push(id);
    sessionStorage.setItem('hiddenBanners', JSON.stringify(hiddenBannerIndexes));
    setLoginMessage((prevLoginMessage) => ({
      ...prevLoginMessage,
      banners: prevLoginMessage.banners.filter((banner) => banner.index !== id),
    }));
  };

  return (
    <div className='banner_area'>
      {loginMessage.banners.length > 0 &&
        loginMessage.banners.map((banner) => (
          <div className='banner_container' key={banner.index}>
            <div className='banner_item'>
              <p className='banner_text'>
                <img
                  src={url('/images/icon_exclamation_banner.svg')}
                  alt='警告アイコン'
                  className='banner_icon'
                />
                <div style={{ paddingRight: '10px' }}>{banner.text}</div>
                <Link
                  href={banner.link.url}
                  className='banner_link'
                  {...(banner.link.type === 'external'
                    ? { rel: 'noopener noreferrer', target: '_blank' }
                    : {})}
                >
                  {banner.link.text}
                </Link>
              </p>
              {banner.is_close_icon && (
                <button className='caution_close_button' onClick={() => handleClose(banner.index)}>
                  <img
                    src={url('/images/icon_close_login-message.svg')}
                    alt='close'
                    style={{ filter: 'none' }}
                  />
                </button>
              )}
            </div>
          </div>
        ))}
    </div>
  );
};

export default LoginMessageBanner;
